import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useFeatureFlagWithLoading } from '@/contexts/FeatureFlagContext';
import { useBadgeNotifications, useMarkBadgeNotificationsRead } from '@/hooks/useBadges';
import { BadgeChip } from './BadgeChip';
import type { BadgeNotification } from '@/services/api';

interface ToastState {
  visible: boolean;
  notifications: BadgeNotification[];
}

export function BadgeNotificationToast() {
  const { isSignedIn } = useAuth();
  const { enabled: showBadges } = useFeatureFlagWithLoading('show_badges');
  const { data: notificationData } = useBadgeNotifications();
  const markRead = useMarkBadgeNotificationsRead();
  const markReadRef = useRef(markRead);
  useEffect(() => {
    markReadRef.current = markRead;
  }, [markRead]);

  const [toasts, setToasts] = useState<ToastState>({ visible: false, notifications: [] });

  useEffect(() => {
    if (!isSignedIn || !showBadges || !notificationData || notificationData.count === 0) {
      return;
    }

    // Show up to 3 notifications
    const notificationsToShow = notificationData.notifications.slice(0, 3);
    setToasts({ visible: true, notifications: notificationsToShow });

    // Auto-dismiss after 5 seconds
    const timeout = setTimeout(() => {
      setToasts({ visible: false, notifications: [] });
      markReadRef.current.mutate();
    }, 5000);

    return () => clearTimeout(timeout);
  }, [isSignedIn, showBadges, notificationData]);

  const handleDismiss = () => {
    setToasts({ visible: false, notifications: [] });
    markRead.mutate();
  };

  // Don't render if not signed in or flag not enabled
  if (!isSignedIn || !showBadges) {
    return null;
  }

  // Don't render if no toasts to show
  if (!toasts.visible || toasts.notifications.length === 0) {
    return null;
  }

  return (
    <div
      className="fixed bottom-4 right-4 z-50 flex flex-col gap-2"
      role="status"
      aria-live="polite"
    >
      {toasts.notifications.map((notification) => (
        <button
          key={notification.id}
          onClick={handleDismiss}
          aria-label={`Dismiss notification: You earned ${notification.name}`}
          className="bg-white border border-sand shadow-lg rounded-lg p-3 cursor-pointer hover:shadow-xl transition-shadow text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2"
          style={{
            fontFamily: "'Source Sans 3', sans-serif",
            fontSize: '13px',
            animation: 'slideUp 0.3s ease-out',
          }}
        >
          <div className="flex items-center gap-2">
            <BadgeChip
              badgeKey={notification.badge_key}
              name={notification.name}
              description={notification.description}
              level={notification.level as 1 | 2 | 3 | 4 | 5}
              emoji={notification.emoji || undefined}
              svgKey={notification.svg_key || undefined}
              size="sm"
              showTooltip={false}
            />
            <span className="text-[#2C2416]">You earned {notification.name}!</span>
          </div>
        </button>
      ))}
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
